import { ThunkMiddleware } from '@reduxjs/toolkit';
import { createSearchParams, NavigateFunction } from 'react-router-dom';
import queryString from 'query-string';

import {
  resetMfaOtpInput,
  mfaRecoverError,
  mfaRecoverResponse,
  mfaVerifyError,
} from '../actions/multi-factor-actions';
import {
  Dispatch,
  Action,
  ThunkAction,
  URLParameters,
  MessageDescriptor,
  SwitchableMfaFactor,
  SwitchMfaFactorError,
} from '../types';
import { AnalyticsClient } from '../utilities/analytics/analytics-web-client';
import { captureException, captureTaggedException } from '../utilities/analytics/error-reporting';
import { addFlag } from '../actions/flag-actions';
import messages from '../containers/MultiFactorPage.messages';
import { RootState } from '../stores/redux.store';
import { getCurrentCloudType } from '../utilities/env';
import { buildInitialState } from '../utilities/init-app-state';
import { resetAppState } from '../actions/app-actions';
import { replaceQueryParam } from '../utilities/applications';

/* *********************
 * id-authentication MFA
 * *********************/
export const idAuthMfaMiddleware: ThunkMiddleware<RootState, Action> =
  ({ dispatch }) =>
  next =>
  action => {
    switch (action.type) {
      case 'MFA_VERIFICATION_VERIFY':
        next(action);
        // The action that calls fetch (/verify) and redirects
        return dispatch(verifyOtpCodeAction(action));
      case 'MFA_VERIFICATION_RESEND_OTP':
        next(action);
        // The action that calls fetch to resend OTP code
        // Transport (i.e. via SMS or Email) is determined by the mfa token.
        return dispatch(resendOtpAction(action));
      case 'MFA_VERIFICATION_RECOVER':
        next(action);
        // The action that verifies recovery code
        return dispatch(verifyRecoveryCodeAction(action));
      case 'MFA_VERIFICATION_CONFIRMED_RECOVER':
        next(action);
        // The action that calls /mfa/authorize and redirects
        return dispatch(confirmedRecoveryCodeAction(action));
      case 'MFA_VERIFICATION_CHANGE_FACTOR':
        next(action);
        // The action that calls /mfa/change to switch MFA factors
        return dispatch(switchMfaFactorAction(action));
      default:
        next(action);
    }
  };

type ResendOtpParams = {
  params: string;
  analyticsClient: AnalyticsClient;
  navigate: NavigateFunction;
  mfaMethod?: string;
};

export const resendOtpAction =
  ({ params, analyticsClient, navigate, mfaMethod }: ResendOtpParams): ThunkAction =>
  async (dispatch, getState) => {
    try {
      const response = await fetch(`/rest/mfa/resend${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getState().csrfToken,
        },
      });

      const queryParams: URLParameters = queryString.parse(params);
      switch (response.status) {
        case 200:
          const { transactionToken } = await response.json();
          navigate(
            {
              search: `?${new URLSearchParams({ transactionToken })}`,
            },
            { replace: true }
          );
          if (mfaMethod === 'email-otp') {
            dispatch(
              addFlag(
                'success',
                messages.mfEmailResentSuccessTitle,
                messages.mfEmailResentSuccessDesc
              )
            );
          } else {
            dispatch(
              addFlag(
                'success',
                messages.mfRecoverySMSResentSuccessTitle,
                messages.mfRecoverySMSResentSuccessDesc
              )
            );
          }
          break;
        case 400:
          redirectToLoginErrorOn400IncorrectTokenResponse({
            analyticsClient,
            page: '2svChallengeScreen',
            subjectId: '2svIncorrectToken',
            currentQueryParams: queryParams,
            navigate,
          });
          break;
        case 422:
          analyticsClient.errorShownEvent('2svChallengeScreen', '2svInvalidPhoneNumber');
          return dispatch(
            addFlag('error', messages.mfVerifyErrorTitle, messages.mfSmsInvalidPhoneNumberDesc)
          );
        case 429:
          analyticsClient.errorShownEvent('2svChallengeScreen', '2svTooManySms');
          handleError(dispatch);
          break;
        default:
          const error = await response.text();
          throw new Error(error || response.statusText);
      }
    } catch (error) {
      analyticsClient.errorShownEvent('2svChallengeScreen', '2svUnknownError');
      handleError(dispatch);
      captureException(error);
    }
  };

type MfaVerificationParams = {
  params: string;
  otpCode: string;
  analyticsClient: AnalyticsClient;
  maybeHardRedirect: (url: string) => void;
};

export const verifyOtpCodeAction =
  ({ params, otpCode, analyticsClient, maybeHardRedirect }: MfaVerificationParams): ThunkAction =>
  async (dispatch, getState) => {
    try {
      const token =
        getCurrentCloudType() === 'commercial'
          ? await import('@castleio/castle-js')
              .then(async ({ createRequestToken }) => await createRequestToken())
              .catch(e => {
                console.error('castle error', e);
                captureTaggedException(e, {
                  capabilities: ['capability-group--login', 'capability-name--mfa'],
                  tags: {
                    castle: 'castle_request_token_error',
                  },
                });
                return '';
              })
          : null;

      const response = await fetch(`/rest/mfa/verify${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getState().csrfToken,
        },
        body: JSON.stringify({ otpCode, ...(token && { token }) }),
      });

      switch (response.status) {
        case 200: {
          const { transactionToken } = await response.json();

          const queryParams: URLParameters = queryString.parse(params);
          queryParams.transactionToken = transactionToken;
          const authorizeResponse = await fetch(
            `/rest/mfa/authorize?${queryString.stringify(queryParams)}`
          );

          if (!authorizeResponse.ok) {
            const error = await authorizeResponse.text();
            throw new Error(error || authorizeResponse.statusText);
          }

          const { redirectUri } = await authorizeResponse.json();
          maybeHardRedirect(redirectUri);

          break;
        }
        case 400: {
          const queryParams: URLParameters = queryString.parse(params);
          redirectToLoginErrorOn400IncorrectTokenResponse({
            analyticsClient,
            page: '2svChallengeScreen',
            subjectId: '2svIncorrectToken',
            currentQueryParams: queryParams,
            navigate: maybeHardRedirect,
          });
          break;
        }
        case 403:
        case 429:
          analyticsClient.errorShownEvent('2svChallengeScreen', '2svIncorrectCode');
          dispatch(mfaVerifyError({ errorCode: 'invalid_otp', message: 'invalid otp code' }));
          break;
        default:
          const error = await response.text();
          throw new Error(error || response.statusText);
      }
    } catch (error) {
      analyticsClient.errorShownEvent('2svChallengeScreen', '2svUnknownError');
      handleError(dispatch);
      captureException(error);
    }
  };

type MfaRecoveryCodeVerificationParams = {
  params: string;
  recoveryCode: string;
  analyticsClient: AnalyticsClient;
  navigate: NavigateFunction;
};

export const verifyRecoveryCodeAction =
  ({
    params,
    recoveryCode,
    analyticsClient,
    navigate,
  }: MfaRecoveryCodeVerificationParams): ThunkAction =>
  async (dispatch, getState) => {
    try {
      const response = await fetch(`/rest/mfa/verify/recoverycode${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getState().csrfToken,
        },
        body: JSON.stringify({ recoveryCode }),
      });

      switch (response.status) {
        case 200:
          const { recoveryCode, transactionToken } = await response.json();
          const newParams = {
            continue: queryString.parse(params).continue,
            transactionToken: transactionToken.toString(),
          };
          navigate(
            {
              search: `?${createSearchParams(newParams)}`,
            },
            { replace: true }
          );
          dispatch(mfaRecoverResponse({ recoveryCode }));
          break;
        case 400:
          const queryParams: URLParameters = queryString.parse(params);
          redirectToLoginErrorOn400IncorrectTokenResponse({
            analyticsClient,
            page: '2svRecoveryKeyScreen',
            subjectId: '2svIncorrectToken',
            currentQueryParams: queryParams,
            navigate,
          });
          break;
        case 403:
        case 429:
          analyticsClient.errorShownEvent('2svRecoveryKeyScreen', '2svIncorrectCode');
          dispatch(
            mfaRecoverError({
              errorCode: 'invalid_recovery_code',
              message: 'invalid recovery code',
            })
          );
          break;
        default:
          const error = await response.text();
          throw new Error(
            `Unexpected response from POST /rest/mfa/verify/recoverycode: ${
              error || response.statusText
            }`
          );
      }
    } catch (error) {
      analyticsClient.errorShownEvent('2svRecoveryKeyScreen', '2svUnknownError');
      handleError(dispatch);
      captureException(error);
    }
  };

type ConfirmedRecoveryCodeParams = {
  params: string;
  analyticsClient: AnalyticsClient;
  maybeHardRedirect: (url: string) => void;
};

export const confirmedRecoveryCodeAction =
  ({ params, analyticsClient, maybeHardRedirect }: ConfirmedRecoveryCodeParams): ThunkAction =>
  async dispatch => {
    try {
      const response = await fetch(`/rest/mfa/authorize${params}`);

      switch (response.status) {
        case 200:
          const { redirectUri } = await response.json();
          maybeHardRedirect(redirectUri);
          break;
        case 400:
        case 403:
          const queryParams: URLParameters = queryString.parse(params);
          redirectToLoginErrorOn400IncorrectTokenResponse({
            analyticsClient,
            page: '2svSavedNewRecoveryKey',
            subjectId: '2svIncorrectToken',
            currentQueryParams: queryParams,
            navigate: maybeHardRedirect,
          });
          break;
        default:
          const error = await response.text();
          throw new Error(
            `Unexpected response from GET /rest/mfa/authorize: ${error || response.statusText}`
          );
      }
    } catch (error) {
      analyticsClient.errorShownEvent('2svSavedNewRecoveryKey', '2svUnknownError');
      handleError(dispatch);
      captureException(error);
    }
  };

type SwitchMfaFactorParams = {
  params: string;
  factor: SwitchableMfaFactor;
  analyticsClient: AnalyticsClient;
  navigate: NavigateFunction;
};

type SwitchMfaFactorErrorFlagMessage = {
  title: MessageDescriptor;
  description: MessageDescriptor;
};

const getSwitchMfaFactorErrorFlagMessages = (
  reason?: SwitchMfaFactorError
): SwitchMfaFactorErrorFlagMessage => {
  if (reason) {
    switch (reason) {
      case 'invalid_factor_type':
      case 'user_not_enrolled':
        return {
          title: messages.mfSwitchFactorInvalidFactorTypeTitle,
          description: messages.mfSwitchFactorInvalidFactorTypeDesc,
        };
      case 'invalid_transaction_token':
        return {
          title: messages.mfSwitchFactorInvalidTokenTitle,
          description: messages.mfSwitchFactorInvalidTokenDesc,
        };
      case 'expired_transaction_token':
        return {
          title: messages.mfSwitchFactorTransactionExpiredTitle,
          description: messages.mfSwitchFactorTransactionExpiredDesc,
        };
    }
  }
  return {
    title: messages.mfSwitchFactorErrorTitle,
    description: messages.mfSwitchFactorErrorDesc,
  };
};

export const switchMfaFactorAction =
  ({ params, factor, analyticsClient, navigate }: SwitchMfaFactorParams): ThunkAction =>
  async dispatch => {
    try {
      const queryParams: URLParameters = queryString.parse(params);
      const transactionToken = queryParams.transactionToken;

      if (!transactionToken) {
        throw new Error('No transaction token found');
      }

      const response = await fetch(
        `/rest/mfa/change?transactionToken=${transactionToken}&factor=${factor}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      switch (response.status) {
        case 200: {
          const { token: newTransactionToken } = await response.json();
          const updatedQueryParamString = replaceQueryParam(
            'transactionToken',
            newTransactionToken
          );

          try {
            const newStateResponse = await fetch(`/rest/login/mfa/state${updatedQueryParamString}`);
            switch (newStateResponse.status) {
              case 200: {
                // This updates the search query params in the address bar, but does not trigger
                // a page reload due to the pathname remains unchanged
                // Setting this would ensure the user loads the page with the updated token should
                // they refresh (eg. F5) the page
                navigate({ search: updatedQueryParamString });

                const newState = await newStateResponse.json();
                const transformedState = await buildInitialState(newState);
                dispatch(resetAppState(transformedState.initialStoreState));
                dispatch(resetMfaOtpInput());
                break;
              }
              default:
                throw Error(
                  `Unexpected status code when fetching new state after switching MFA factor: ${response.status}`
                );
            }
          } catch (error) {
            dispatch(
              addFlag('error', messages.mfSwitchFactorErrorTitle, messages.mfSwitchFactorErrorDesc)
            );
            captureException(error);
          }
          break;
        }
        case 400: {
          analyticsClient.errorShownEvent('2svChallengeScreen', '2svSwitchFactorBadRequest');
          try {
            const errorResponse = await response.json();
            const { title, description } = getSwitchMfaFactorErrorFlagMessages(
              errorResponse?.context?.reason
            );
            dispatch(addFlag('error', title, description));
          } catch (error) {
            dispatch(
              addFlag('error', messages.mfSwitchFactorErrorTitle, messages.mfSwitchFactorErrorDesc)
            );
            captureException(error);
          }
          break;
        }
        case 429: {
          analyticsClient.errorShownEvent('2svChallengeScreen', '2svSwitchFactorTooManyAttempts');
          dispatch(
            addFlag(
              'error',
              messages.mfSwitchFactorTooManyAttemptsTitle,
              messages.mfSwitchFactorTooManyAttemptsDesc
            )
          );
          break;
        }
        default:
          const error = await response.text();
          throw new Error(error || response.statusText);
      }
    } catch (error) {
      analyticsClient.errorShownEvent('2svChallengeScreen', '2svSwitchFactorUnknownError');
      dispatch(
        addFlag('error', messages.mfSwitchFactorErrorTitle, messages.mfSwitchFactorErrorDesc)
      );
      captureException(error);
    }
  };

function handleError(dispatch: Dispatch) {
  return dispatch(addFlag('error', messages.mfVerifyErrorTitle, messages.mfVerifyErrorDesc));
}

function redirectToLoginErrorOn400IncorrectTokenResponse({
  analyticsClient,
  page,
  subjectId,
  currentQueryParams,
  navigate,
}: {
  analyticsClient: AnalyticsClient;
  page: string;
  subjectId: string;
  currentQueryParams: URLParameters;
  navigate: (url: string) => void;
}) {
  analyticsClient.errorShownEvent(page, subjectId);
  delete currentQueryParams.transactionToken;
  delete currentQueryParams.errorCode;
  const currentQueryString = queryString.stringify(currentQueryParams);
  const extraParamsString = currentQueryString === '' ? '' : `&${currentQueryString}`;

  navigate(`/login?prompt=true&errorCode=login.form.token.expired${extraParamsString}`);
}
